import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { ColDef } from 'ag-grid-community';
import {
  TableActionEvent,
  TableConfig,
} from '../../../shared/components/table/table.interfaces';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { ActionConfig } from '../../../shared/components/table/table-action-configs';
import { InventoryFormsComponent } from '../inventory/inventory-forms/inventory-forms.component';
import { AssetManagementService } from '../asset-management-service';
import {
  InventoryTableData,
  InventoryOrderTableData,
  InventoryHistoryData,
} from '@app/shared/interfaces/inventory-data';
import { ToastrService } from 'ngx-toastr';
import { DateUtils, getMonthDateRange } from '@app/shared/utils/date.utils';
import { VendorStatsCardEnum } from '@app/shared/constants/global.enum';
import { yearDateFormat } from '@app/shared/constants/dateFormat';

@Injectable({
  providedIn: 'root',
})
export class InventoryTableService {
  // Inventory Table Subjects and Observables
  private readonly dataSubject = new BehaviorSubject<InventoryTableData[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly selectedRowsSubject = new BehaviorSubject<
    InventoryTableData[]
  >([]);

  public readonly data$ = this.dataSubject.asObservable();
  public readonly loading$ = this.loadingSubject.asObservable();
  public readonly selectedRows$ = this.selectedRowsSubject.asObservable();

  // Procured (Orders tab) Table Subjects and Observables
  private readonly dataSubjectProcured = new BehaviorSubject<
    InventoryOrderTableData[]
  >([]);
  private readonly loadingSubjectProcured = new BehaviorSubject<boolean>(false);
  private readonly selectedRowsSubjectProcured = new BehaviorSubject<
    InventoryOrderTableData[]
  >([]);

  public readonly dataProcured$ = this.dataSubjectProcured.asObservable();
  public readonly loadingProcured$ = this.loadingSubjectProcured.asObservable();
  public readonly selectedRowsProcured$ =
    this.selectedRowsSubjectProcured.asObservable();

  public readonly tableConfig: TableConfig = {
    enableRowSelection: true,
    enableActions: true,
    enableSorting: true,
    enableFiltering: true,
    enableExport: true,
  };

  inventorySummaryData: any[] = [];
  procuredSummaryData: any[] = [];
  inventoryHistoryData: InventoryHistoryData[] = [];
  VendorStats = VendorStatsCardEnum;

  currentStartDate: string = '';
  currentEndDate: string = '';
  currentLocation: string = '';
  // Orders (procured) tab has its own location filter, independent of the
  // Accessories Management tab's location, and always defaults to 'All'.
  currentProcuredLocation: string = 'All';
  // Orders (procured) tab also has its own date range, independent of the
  // Accessories Management tab's date range. Without this, both tabs shared
  // currentStartDate/currentEndDate, so a date range picked on one tab was
  // silently sent to the API when switching to the other tab, even though
  // the date-range picker itself visually reset to the whole month.
  currentOrderStartDate: string = '';
  currentOrderEndDate: string = '';

  public readonly actions: ActionConfig[] = [
    { action: 'edit', label: 'Edit Record', icon: 'edit', visible: true },
    {
      action: 'repair',
      label: 'Initiate Repair',
      icon: 'build',
      visible: (data) => data.repairStatus === 'Pending Assessment',
    },
  ];

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private assetManagementService: AssetManagementService,
    private toasterService: ToastrService,
  ) {
    // Default location to 'All' for accessories management
    this.currentLocation = 'All';
    this.currentProcuredLocation = 'All';
    this.procuredSummaryData = [];
    this.inventorySummaryData = [];
    const now = new Date();
    const defaultMonth = `${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getFullYear()}`;
    const selectedMonth =
      history.state?.selectedMonth &&
      typeof history.state.selectedMonth === 'string'
        ? history.state.selectedMonth
        : defaultMonth;

    const { startDate, endDate } = getMonthDateRange(
      selectedMonth,
      yearDateFormat.date,
    );
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    this.currentOrderStartDate = startDate;
    this.currentOrderEndDate = endDate;

    (window as any).openInventoryProductView = (itemId: any) => {
      console.log('Opening inventory product view for itemId:', itemId);
      this.openViewDialogByItemId(itemId);
    };

    (window as any).openInvoice = (procuredOrderId: number) => {
      this.openInvoiceDialog(procuredOrderId);
    };
  }

  /**
   * Returns the current whole-month date range (same default the
   * date-range picker shows on a fresh mount), used to reset a tab's
   * date filter whenever that tab is (re)loaded, e.g. on tab switch.
   */
  private getDefaultMonthDateRange(): { startDate: string; endDate: string } {
    return getMonthDateRange('', yearDateFormat.date);
  }

  public loadInitialData(selectedCard: string, location?: string): void {
    if (selectedCard === 'inventory') {
      const effectiveLocation = location || this.currentLocation || 'All';
      this.currentLocation = effectiveLocation;
      // Reset to the whole current month whenever this tab is loaded fresh
      // (e.g. switching tabs), instead of reusing a stale date range that
      // may have been left over from the Orders tab.
      const { startDate, endDate } = this.getDefaultMonthDateRange();
      this.currentStartDate = startDate;
      this.currentEndDate = endDate;
      this.getInventorySummaryData(
        this.currentStartDate,
        this.currentEndDate,
        effectiveLocation,
      );
    } else if (selectedCard === 'inventory-order') {
      const effectiveLocation =
        location || this.currentProcuredLocation || 'All';
      this.currentProcuredLocation = effectiveLocation;
      // Same reset, scoped to the Orders tab's own date state so it never
      // leaks into/from the Accessories Management tab.
      const { startDate, endDate } = this.getDefaultMonthDateRange();
      this.currentOrderStartDate = startDate;
      this.currentOrderEndDate = endDate;
      this.getProcuredSummaryData(
        this.currentOrderStartDate,
        this.currentOrderEndDate,
        effectiveLocation,
      );
    }
  }

  getCurrentLocation(): string {
    const navigationLocation = history.state.selectedLocation;
    return navigationLocation ? navigationLocation : this.currentLocation;
  }

  getCurrentProcuredLocation(): string {
    return this.currentProcuredLocation || 'All';
  }

  // updateLocationAndRefresh(location: string): void {
  //   this.currentLocation = location;
  //   this.getInventorySummaryData(
  //     this.currentStartDate,
  //     this.currentEndDate,
  //     location,
  //   );
  // }

  getInventorySummaryData(
    startDate: string,
    endDate: string,
    location: string,
  ): void {
    if (startDate !== '') this.currentStartDate = startDate;
    if (endDate !== '') this.currentEndDate = endDate;
    if (location !== '') this.currentLocation = location;
    this.assetManagementService
      .getInventoryTableData(
        this.currentStartDate,
        this.currentEndDate,
        this.currentLocation,
      )
      .subscribe({
        next: (inventorySummary) => {
          this.inventorySummaryData = inventorySummary;
          this.dataSubject.next(this.inventorySummaryData);
        },
        error: (error: any) => {
          this.toasterService.error('Failed to fetch inventory data');
        },
      });
  }

  getProcuredSummaryData(
    startDate: string,
    endDate: string,
    location: string,
  ): void {
    if (startDate !== '') this.currentOrderStartDate = startDate;
    if (endDate !== '') this.currentOrderEndDate = endDate;
    if (location !== '') this.currentProcuredLocation = location;
    this.assetManagementService
      .getProcuredSummaryData(
        this.currentOrderStartDate,
        this.currentOrderEndDate,
        this.currentProcuredLocation,
      )
      .subscribe({
        next: (procuredSummary) => {
          this.procuredSummaryData = procuredSummary;
          this.dataSubjectProcured.next(this.procuredSummaryData);
        },
        error: (error: any) => {
          this.toasterService.error('Failed to fetch procured summary data');
        },
      });
  }

  openProcureItemDialog(): void {
    this.dialog.open(InventoryFormsComponent, {
      panelClass: 'inventory-procure-item-dialog',
      data: {
        mode: 'procure-item',
        rowData: {},
      },
    });
  }

  openBulkIssueUploadDialog(): void {
    const dialogRef = this.dialog.open(InventoryFormsComponent, {
      panelClass: 'inventory-bulk-issue-dialog',
      data: {
        mode: 'bulk-issue-upload',
        rowData: {},
      },
    });

    dialogRef.afterClosed().subscribe((result: boolean | undefined) => {
      if (result) {
        this.getInventorySummaryData(
          this.currentStartDate,
          this.currentEndDate,
          this.currentLocation,
        );
      }
    });
  }

  openBulkProcuredUploadDialog(): void {
    const dialogRef = this.dialog.open(InventoryFormsComponent, {
      panelClass: 'inventory-bulk-issue-dialog',
      data: {
        mode: 'bulk-issue-upload',
        rowData: {},
        isOrder: true,
      },
    });

    dialogRef.afterClosed().subscribe((result: boolean | undefined) => {
      if (result) {
        this.getProcuredSummaryData(
          this.currentOrderStartDate,
          this.currentOrderEndDate,
          this.currentProcuredLocation,
        );
      }
    });
  }

  openIssueNewItemFormDialog(): void {
    const dialogRef = this.dialog.open(InventoryFormsComponent, {
      panelClass: 'inventory-edit-form',
      data: {
        mode: 'issue-new-item',
        rowData: {},
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: InventoryOrderTableData | undefined) => {
        if (result) {
          // "Issue New Item" belongs to the Accessories Management tab, so
          // refresh via issued-order-summary using that tab's own date
          // range/location — not the Orders tab's procurement-summary call.
          this.getInventorySummaryData(
            this.currentStartDate,
            this.currentEndDate,
            this.currentLocation,
          );
        }
      });
  }

  openAddNewFormDialog(): void {
    const dialogRef = this.dialog.open(InventoryFormsComponent, {
      panelClass: 'inventory-add-new-dialog',
      data: {
        mode: 'add',
        rowData: {},
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: InventoryOrderTableData | undefined) => {
        if (result) {
          this.getProcuredSummaryData(
            this.currentOrderStartDate,
            this.currentOrderEndDate,
            this.currentProcuredLocation,
          );
        }
      });
  }

  openViewDialog(row: any) {
    console.log('Opening view dialog for row:', row);
    if (!row?.productId) {
      console.warn('No itemId in row; cannot load history');
      return;
    }

    const size = row.size !== 'N/A' ? row.size : undefined;

    this.assetManagementService
      .getInventoryItemHistory(
        row.productId,
        row.subProductId,
        size,
        row.location,
      )
      .subscribe({
        next: (history) => {
          this.dialog.open<InventoryFormsComponent>(InventoryFormsComponent, {
            panelClass: 'inventory-view-details-dialog',
            data: {
              mode: 'view',
              rowData: history.metadata,
              inventoryHistoryData: history.data,
            },
          });
        },
        error: (error: any) => {
          this.toasterService.error('Failed to load item history.');
        },
      });
  }

  openInvoiceDialog(
    procuredOrderId: number,
    mode?: 'add-invoice' | 'view-invoice',
  ) {
    const order = this.procuredSummaryData.find(
      (r) => r.procuredOrderId === procuredOrderId,
    );

    if (!order) {
      this.toasterService.error('Order not found');
      return;
    }

    const finalMode =
      mode || (order.ordersAttachmentUrl ? 'view-invoice' : 'add-invoice');
    const panelClass =
      finalMode === 'view-invoice'
        ? 'invoice-view-dialog'
        : 'invoice-add-dialog';

    const dialogConfig = {
      panelClass: panelClass,
      data: {
        mode: finalMode,
        rowData: order,
      },
    };
    this.dialog.open(InventoryFormsComponent, dialogConfig);
  }

  openViewDialogByItemId(itemId: any) {
    const record = this.dataSubject.value.find((r) => r.itemId === itemId);
    if (!itemId) return;
    this.openViewDialog(itemId);
  }

  handleAction(action: string, rowData: any): void {
    switch (action) {
      case 'edit':
        this.openEditIssueDialog(rowData);
        break;
      default:
        break;
    }
  }

  openEditIssueDialog(rowData: any): void {
    const dialogRef = this.dialog.open(InventoryFormsComponent, {
      panelClass: 'inventory-edit-form',
      data: {
        mode: 'issue-new-item',
        rowData: rowData,
        isEdit: true,
      },
    });

    dialogRef.afterClosed().subscribe((result: boolean | undefined) => {
      if (result) {
        this.getInventorySummaryData(
          this.currentStartDate,
          this.currentEndDate,
          this.currentLocation,
        );
      }
    });
  }
}